import React, { useState } from 'react';
import { X, Plus, Search } from 'lucide-react';
import { supabase } from '@shared/lib/supabase';
import { toast } from '@shared/components/ui/Toaster';

interface AddCustomSkillModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

interface NewSkillForm {
  skill_id: string;
  skill_name: string;
  category: string;
  subcategory: string;
  priority: number;
  current_proficiency_level: string;
  target_proficiency_level: string;
  estimated_months_to_complete: number;
  development_notes: string;
  use_existing_skill: boolean;
}

const categories = [
  { value: 'Technical', label: 'Technical' },
  { value: 'Leadership', label: 'Leadership' },
  { value: 'Soft Skills', label: 'Soft Skills' },
  { value: 'Industry', label: 'Industry' },
  { value: 'General', label: 'General' }
];

const proficiencyLevels = ['none', 'beginner', 'intermediate', 'advanced', 'expert'];

export const AddCustomSkillModal: React.FC<AddCustomSkillModalProps> = ({ isOpen, onClose, onSuccess }) => {
  const [formData, setFormData] = useState<NewSkillForm>({
    skill_id: '',
    skill_name: '',
    category: 'Technical',
    subcategory: '',
    priority: 3,
    current_proficiency_level: 'none',
    target_proficiency_level: 'intermediate',
    estimated_months_to_complete: 6,
    development_notes: '',
    use_existing_skill: false
  });

  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [searching, setSearching] = useState(false);
  const [saving, setSaving] = useState(false);

  const handleChange = (field: keyof NewSkillForm, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const searchSkills = async (searchTerm: string) => {
    if (!searchTerm || searchTerm.length < 2) {
      setSearchResults([]);
      return;
    }

    setSearching(true);
    try {
      const { data, error } = await supabase
        .from('skills_master')
        .select('*')
        .ilike('skill_name', `%${searchTerm}%`)
        .eq('is_active', true)
        .limit(10);

      if (error) throw error;
      setSearchResults(data || []);
    } catch (error) {
      console.error('Error searching skills:', error);
    } finally {
      setSearching(false);
    }
  };

  const selectExistingSkill = (skill: any) => {
    setFormData(prev => ({
      ...prev,
      skill_id: skill.id,
      skill_name: skill.skill_name,
      category: skill.category,
      subcategory: skill.subcategory || '',
      use_existing_skill: true
    }));
    setSearchResults([]);
  };

  const validateForm = (): string | null => {
    if (!formData.skill_name.trim()) {
      return 'Skill name is required';
    }

    if (formData.skill_name.length < 2) {
      return 'Skill name must be at least 2 characters';
    }

    if (formData.priority < 1 || formData.priority > 5) {
      return 'Priority must be between 1 and 5';
    }

    const currentIndex = proficiencyLevels.indexOf(formData.current_proficiency_level);
    const targetIndex = proficiencyLevels.indexOf(formData.target_proficiency_level);

    if (targetIndex <= currentIndex) {
      return 'Target proficiency must be higher than current proficiency';
    }

    if (formData.estimated_months_to_complete < 1) {
      return 'Timeline must be at least 1 month';
    }

    return null;
  };

  const handleSubmit = async () => {
    const validationError = validateForm();
    if (validationError) {
      toast.error(validationError);
      return;
    }

    setSaving(true);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      let skillId = formData.skill_id;

      // If using a custom skill name (not from master), create it in skills_master first
      if (!formData.use_existing_skill) {
        const { data: existingSkill } = await supabase
          .from('skills_master')
          .select('id')
          .ilike('skill_name', formData.skill_name)
          .maybeSingle();

        if (existingSkill) {
          skillId = existingSkill.id;
        } else {
          const { data: newSkill, error: createError } = await supabase
            .from('skills_master')
            .insert({
              skill_name: formData.skill_name,
              category: formData.category,
              subcategory: formData.subcategory || null,
              market_demand_score: 50,
              is_active: true
            })
            .select('id')
            .single();

          if (createError) throw createError;
          skillId = newSkill.id;
        }
      }

      // Check if user already has this skill
      const { data: existingUserSkill } = await supabase
        .from('user_skills_tracker')
        .select('id')
        .eq('user_id', user.id)
        .eq('skill_id', skillId)
        .eq('is_deleted', false)
        .maybeSingle();

      if (existingUserSkill) {
        toast.error('You already have this skill in your tracker');
        setSaving(false);
        return;
      }

      // Add skill to user's tracker
      const { error: insertError } = await supabase
        .from('user_skills_tracker')
        .insert({
          user_id: user.id,
          skill_id: skillId,
          source: 'user_added',
          priority: formData.priority,
          current_proficiency_level: formData.current_proficiency_level,
          target_proficiency_level: formData.target_proficiency_level,
          progress_percentage: 0,
          estimated_months_to_complete: formData.estimated_months_to_complete,
          development_notes: formData.development_notes,
          status: 'active'
        });

      if (insertError) throw insertError;

      // Log the modification
      await supabase.from('user_skill_modifications').insert({
        user_id: user.id,
        skill_id: skillId,
        modification_type: 'skill_added',
        new_state: formData,
        reason_code: 'user_preference',
        was_ai_suggested: false
      });

      toast.success('Skill added successfully!');
      resetForm();
      onSuccess();
      onClose();
    } catch (error: any) {
      console.error('Error adding skill:', error);
      toast.error(error.message || 'Failed to add skill');
    } finally {
      setSaving(false);
    }
  };

  const resetForm = () => {
    setFormData({
      skill_id: '',
      skill_name: '',
      category: 'Technical',
      subcategory: '',
      priority: 3,
      current_proficiency_level: 'none',
      target_proficiency_level: 'intermediate',
      estimated_months_to_complete: 6,
      development_notes: '',
      use_existing_skill: false
    });
    setSearchResults([]);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={handleClose} />

        <div className="inline-block w-full max-w-2xl my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-lg">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
            <h3 className="text-xl font-semibold text-gray-900">Add New Skill</h3>
            <button
              onClick={handleClose}
              className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <div className="px-6 py-6 space-y-6 max-h-[70vh] overflow-y-auto">
            {/* Search Existing Skills */}
            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">
                Search Existing Skills
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  value={formData.use_existing_skill ? formData.skill_name : ''}
                  onChange={(e) => {
                    handleChange('skill_name', e.target.value);
                    handleChange('use_existing_skill', false);
                    searchSkills(e.target.value);
                  }}
                  placeholder="Search for a skill..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  disabled={formData.use_existing_skill}
                />
              </div>

              {searching && (
                <p className="text-sm text-gray-500 mt-2">Searching...</p>
              )}

              {searchResults.length > 0 && (
                <div className="mt-2 border border-gray-200 rounded-lg max-h-48 overflow-y-auto">
                  {searchResults.map(skill => (
                    <button
                      key={skill.id}
                      onClick={() => selectExistingSkill(skill)}
                      className="w-full px-4 py-3 text-left hover:bg-gray-50 border-b border-gray-100 last:border-0"
                    >
                      <p className="font-medium text-gray-900">{skill.skill_name}</p>
                      <p className="text-sm text-gray-500">
                        {skill.category} {skill.subcategory && `• ${skill.subcategory}`}
                      </p>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-200" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-gray-500">or create a custom skill</span>
              </div>
            </div>

            {/* Custom Skill Name */}
            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">
                Skill Name *
              </label>
              <input
                type="text"
                value={formData.skill_name}
                onChange={(e) => handleChange('skill_name', e.target.value)}
                placeholder="e.g., Advanced Python Programming"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              />
            </div>

            {/* Category and Subcategory */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-700 mb-2 block">
                  Category *
                </label>
                <select
                  value={formData.category}
                  onChange={(e) => handleChange('category', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  disabled={formData.use_existing_skill}
                >
                  {categories.map(cat => (
                    <option key={cat.value} value={cat.value}>
                      {cat.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700 mb-2 block">
                  Subcategory
                </label>
                <input
                  type="text"
                  value={formData.subcategory}
                  onChange={(e) => handleChange('subcategory', e.target.value)}
                  placeholder="e.g., Programming Languages"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  disabled={formData.use_existing_skill}
                />
              </div>
            </div>

            {/* Priority */}
            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">
                Priority *
              </label>
              <div className="flex gap-2">
                {[1, 2, 3, 4, 5].map(level => (
                  <button
                    key={level}
                    onClick={() => handleChange('priority', level)}
                    className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors ${
                      formData.priority === level
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {level}
                  </button>
                ))}
              </div>
              <div className="flex justify-between text-xs text-gray-500 mt-1">
                <span>Critical</span>
                <span>High</span>
                <span>Medium</span>
                <span>Low</span>
                <span>Optional</span>
              </div>
            </div>

            {/* Proficiency Levels */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-700 mb-2 block">
                  Current Level *
                </label>
                <select
                  value={formData.current_proficiency_level}
                  onChange={(e) => handleChange('current_proficiency_level', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  {proficiencyLevels.map(level => (
                    <option key={level} value={level} className="capitalize">
                      {level}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700 mb-2 block">
                  Target Level *
                </label>
                <select
                  value={formData.target_proficiency_level}
                  onChange={(e) => handleChange('target_proficiency_level', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  {proficiencyLevels.map(level => (
                    <option key={level} value={level} className="capitalize">
                      {level}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Timeline */}
            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">
                Estimated Timeline (months) *
              </label>
              <input
                type="number"
                min="1"
                max="60"
                value={formData.estimated_months_to_complete}
                onChange={(e) => handleChange('estimated_months_to_complete', parseInt(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* Development Notes */}
            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">
                Development Notes
              </label>
              <textarea
                value={formData.development_notes}
                onChange={(e) => handleChange('development_notes', e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                placeholder="Add your learning goals, resources, or motivation..."
              />
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-gray-50">
            <button
              onClick={handleClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={saving}
              className={`px-6 py-2 text-sm font-medium text-white rounded-lg transition-colors flex items-center gap-2 ${
                saving ? 'bg-gray-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'
              }`}
            >
              <Plus className="w-4 h-4" />
              {saving ? 'Adding...' : 'Add Skill'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
